import {
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    View
} from "react-native";
import type {
    BoardMemberPayloadType,
    BoardMembersViewProps
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextInput,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import {
    CameraIcon,
    PencilIcon,
    Trash2Icon,
    PlusIcon,
    UserIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";
import SmartCoverImage from "../../../../components/smartCoverImage";

const BoardMembersView = ({
    refreshKey
}: BoardMembersViewProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const photoInputRef = useRef<HTMLInputElement>(null);

    const [
        boardMembers,
        setBoardMembers
    ] = useState<BoardMemberPayloadType[]>([]);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isFormDialogVisible,
        setIsFormDialogVisible
    ] = useState<boolean>(false);

    const [
        isDeleteDialogVisible,
        setIsDeleteDialogVisible
    ] = useState<boolean>(false);

    const [
        photoFile,
        setPhotoFile
    ] = useState<File | null>(null);

    const [
        photoPreview,
        setPhotoPreview
    ] = useState<string>("");

    const [
        selectedMember,
        setSelectedMember
    ] = useState<BoardMemberPayloadType | null>(null);

    const [
        fullName,
        setFullName
    ] = useState<string>("");

    const [
        position,
        setPosition
    ] = useState<string>("");

    const [
        order,
        setOrder
    ] = useState<string>("0");

    const [
        profilePhotoPath,
        setProfilePhotoPath
    ] = useState<string>("");

    const getImageUrl = (path?: string) => {
        if(!path) {
            return "";
        }
        if(path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:")) {
            return path;
        }
        return `${process.env.STORAGE_URL}/uploads${path.startsWith("/") ? "" : "/"}${path}`;
    };

    const uploadPhoto = async (file: File): Promise<string> => {
        const tokenResult = await RESTServiceContext.action("getUploadTokenForContentPhoto", {
            fileType: file.type || "image/jpeg",
            fileName: file.name,
            fileSize: file.size
        });
        const uploadToken = tokenResult.payload?.token;
        if(!uploadToken) {
            throw new Error("No upload token received");
        }
        const formData = new FormData();
        formData.append("file", file);
        const uploadResponse = await fetch(`${process.env.STORAGE_URL}/upload`, {
            headers: {
                Authorization: uploadToken
            },
            body: formData,
            method: "POST"
        });
        if(!uploadResponse.ok) {
            throw new Error("Storage upload failed");
        }
        const uploadJson = await uploadResponse.json();
        return uploadJson?.payload?.key || "";
    };

    const fetchBoardMembers = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await RESTServiceContext.action("getAllBoardMembers", null);
            if(Array.isArray(response?.payload)) {
                const sorted = [...response.payload].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                setBoardMembers(sorted);
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("board-members-fetch-error")
            });
        } finally {
            setIsLoading(false);
        }
    }, [
        localize
    ]);

    useEffect(() => {
        fetchBoardMembers();
    }, [
        fetchBoardMembers,
        refreshKey
    ]);

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if(file) {
            const maxFileSize = 5 * 1024 * 1024;
            if(file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handleRemovePhoto = () => {
        setProfilePhotoPath("");
        setPhotoPreview("");
        setPhotoFile(null);
        if(photoInputRef.current) {
            photoInputRef.current.value = "";
        }
    };

    const handleOpenCreate = () => {
        setSelectedMember(null);
        setFullName("");
        setPosition("");
        setOrder(String(boardMembers.length + 1));
        setProfilePhotoPath("");
        setPhotoFile(null);
        setPhotoPreview("");
        if(photoInputRef.current) {
            photoInputRef.current.value = "";
        }
        setIsFormDialogVisible(true);
    };

    const handleOpenEdit = (member: BoardMemberPayloadType) => {
        setSelectedMember(member);
        setFullName(member.fullName || "");
        setPosition(member.position || "");
        setOrder(String(member.order ?? 0));
        setProfilePhotoPath(member.profilePhotoPath || "");
        setPhotoFile(null);
        setPhotoPreview("");
        if(photoInputRef.current) {
            photoInputRef.current.value = "";
        }
        setIsFormDialogVisible(true);
    };

    const handleSave = async () => {
        if(!fullName.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("board-member-name-required")
            });
            return;
        }

        if(!position.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("board-member-position-required")
            });
            return;
        }

        setIsSubmitting(true);
        try {
            let finalPhotoPath = profilePhotoPath;
            if(photoFile) {
                const uploadedKey = await uploadPhoto(photoFile);
                if(uploadedKey) {
                    finalPhotoPath = uploadedKey;
                }
            }

            const parsedOrder = Number(order) || 0;
            if(selectedMember) {
                const response = await RESTServiceContext.action("updateBoardMember", {
                    profilePhotoPath: finalPhotoPath || undefined,
                    id: selectedMember._id,
                    order: parsedOrder,
                    position,
                    fullName
                });

                if(response?.isError) {
                    NCoreUIKitSnackBar.open({
                        title: response?.message || localize("board-member-save-error")
                    });
                    return;
                }

                NCoreUIKitSnackBar.open({
                    title: localize("board-member-update-success")
                });
            } else {
                const response = await RESTServiceContext.action("createBoardMember", {
                    profilePhotoPath: finalPhotoPath || undefined,
                    order: parsedOrder,
                    position,
                    fullName
                });

                if(response?.isError) {
                    NCoreUIKitSnackBar.open({
                        title: response?.message || localize("board-member-save-error")
                    });
                    return;
                }

                NCoreUIKitSnackBar.open({
                    title: localize("board-member-create-success")
                });
            }

            setPhotoFile(null);
            setPhotoPreview("");
            setIsFormDialogVisible(false);
            fetchBoardMembers();
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("board-member-save-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if(!selectedMember) {
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await RESTServiceContext.action("deleteBoardMember", {
                id: selectedMember._id
            });

            if(response?.isError) {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("board-member-delete-error")
                });
                return;
            }

            NCoreUIKitSnackBar.open({
                title: localize("board-member-delete-success")
            });
            setIsDeleteDialogVisible(false);
            fetchBoardMembers();
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("board-member-delete-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingMd
                }
            ]}
        >
            <View>
                <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {localize("board-members-title")}
                </Text>
                <Text
                    variant="bodySmallSize"
                    color="disabled"
                >
                    {`${boardMembers.length} ${localize("board-members-count")}`}
                </Text>
            </View>
            <Button
                customTextColor={colors.project.brandWhite}
                title={localize("board-members-add-new")}
                icon={({
                    size
                }) => <PlusIcon
                    color={colors.project.brandWhite}
                    size={size}
                />}
                onPress={handleOpenCreate}
                spreadBehaviour="free"
                variant="filled"
                type="primary"
            />
        </View>;
    };

    const renderEmpty = () => {
        return <View
            style={[
                stylesheet.emptyContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl
                }
            ]}
        >
            <Text
                variant="bodyMediumSize"
                color="disabled"
            >
                {localize("board-members-empty")}
            </Text>
        </View>;
    };

    const renderList = () => {
        if(boardMembers.length === 0) {
            return renderEmpty();
        }

        return <View
            style={[
                stylesheet.listContainer,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            {boardMembers.map((member) => {
                const photoUrl = getImageUrl(member.profilePhotoPath);

                return <View
                    style={[
                        stylesheet.memberCard,
                        {
                            backgroundColor: colors.project.brandWhite,
                            borderColor: colors.project.brandBorder,
                            borderWidth: borders.seperator,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingMd
                        }
                    ]}
                    key={member._id}
                >
                    <View
                        style={[
                            stylesheet.memberInfo,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.memberPhotoBox,
                                {
                                    backgroundColor: colors.content.container.subtle,
                                    borderColor: colors.content.border.subtle,
                                    borderRadius: radiuses.sm
                                }
                            ]}
                        >
                            {
                                photoUrl ?
                                    <SmartCoverImage
                                        containerStyle={stylesheet.memberPhoto}
                                        imageStyle={stylesheet.memberPhoto}
                                        url={photoUrl}
                                    />
                                :
                                    <UserIcon
                                        color={colors.content.text.disabled}
                                        size={24}
                                    />
                            }
                        </View>
                        <View
                            style={[
                                stylesheet.memberDetails,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                variant="titleSmallSize"
                                color="high"
                            >
                                {member.fullName}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                color="disabled"
                            >
                                {member.position}
                            </Text>
                        </View>
                    </View>
                    <View
                        style={[
                            stylesheet.actionsContainer,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.orderBadge,
                                {
                                    backgroundColor: colors.content.container.subtle,
                                    paddingHorizontal: spaces.spacingSm,
                                    borderRadius: radiuses.full
                                }
                            ]}
                        >
                            <Text
                                variant="bodySmallSize"
                                color="disabled"
                            >
                                #{member.order ?? 0}
                            </Text>
                        </View>
                        <Button
                            icon={({
                                color,
                                size
                            }) => <PencilIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            onPress={() => handleOpenEdit(member)}
                            spreadBehaviour="free"
                            variant="ghost"
                        />
                        <Button
                            icon={({
                                color,
                                size
                            }) => <Trash2Icon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            onPress={() => {
                                setSelectedMember(member);
                                setIsDeleteDialogVisible(true);
                            }}
                            spreadBehaviour="free"
                            variant="ghost"
                            type="danger"
                        />
                    </View>
                </View>;
            })}
        </View>;
    };

    const renderFormDialog = () => {
        const displayPhotoUrl = photoPreview || getImageUrl(profilePhotoPath);

        return <Dialog
            title={selectedMember ? localize("board-member-edit-title") : localize("board-member-create-title")}
            primaryButtonProps={{
                title: selectedMember ? localize("save") || "Kaydet" : localize("create") || "Oluştur",
                isLoading: isSubmitting,
                onPress: handleSave
            }}
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                onPress: () => setIsFormDialogVisible(false),
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onClosed={() => setIsFormDialogVisible(false)}
            isVisible={isFormDialogVisible}
            variant="yes-no"
        >
            <View
                style={[
                    stylesheet.dialogForm,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <input
                    style={stylesheet.photoInput}
                    onChange={handlePhotoSelect}
                    ref={photoInputRef}
                    accept="image/*"
                    type="file"
                />
                <TouchableOpacity
                    style={[
                        stylesheet.dialogPhotoBox,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderColor: colors.content.border.subtle,
                            borderRadius: radiuses.md
                        }
                    ]}
                    onPress={() => photoInputRef.current?.click()}
                >
                    {
                        displayPhotoUrl ?
                            <SmartCoverImage
                                containerStyle={stylesheet.memberPhoto}
                                imageStyle={stylesheet.memberPhoto}
                                url={displayPhotoUrl}
                            />
                        :
                            <View
                                style={[
                                    stylesheet.photoPlaceholderContent,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <CameraIcon
                                    color={colors.content.text.disabled}
                                    size={28}
                                />
                                <Text
                                    variant="bodySmallSize"
                                    color="disabled"
                                >
                                    {localize("content-select-photo")}
                                </Text>
                            </View>
                    }
                </TouchableOpacity>
                {Boolean(displayPhotoUrl) && <Button
                    title={localize("content-remove-photo")}
                    icon={({
                        color,
                        size
                    }) => <Trash2Icon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    onPress={handleRemovePhoto}
                    spreadBehaviour="free"
                    variant="filled"
                    type="danger"
                />}
                <TextInput
                    placeholder={localize("board-member-fullname-placeholder")}
                    title={localize("board-member-fullname-label")}
                    onChangeText={setFullName}
                    spreadBehaviour="stretch"
                    value={fullName}
                />
                <TextInput
                    placeholder={localize("board-member-position-placeholder")}
                    title={localize("board-member-position-label")}
                    onChangeText={setPosition}
                    spreadBehaviour="stretch"
                    value={position}
                />
                <TextInput
                    placeholder={localize("board-member-order-placeholder")}
                    title={localize("board-member-order-label")}
                    spreadBehaviour="stretch"
                    onChangeText={setOrder}
                    keyboardType="numeric"
                    value={order}
                />
            </View>
        </Dialog>;
    };

    const renderDeleteDialog = () => {
        return <Dialog
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                onPress: () => setIsDeleteDialogVisible(false),
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onClosed={() => setIsDeleteDialogVisible(false)}
            title={localize("board-member-delete-title")}
            primaryButtonProps={{
                title: localize("delete") || "Sil",
                isLoading: isSubmitting,
                onPress: handleDelete,
                type: "danger"
            }}
            isVisible={isDeleteDialogVisible}
            variant="yes-no"
        >
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {`${selectedMember?.fullName || ""} ${localize("board-member-delete-confirm")}`}
            </Text>
        </Dialog>;
    };

    if(isLoading) {
        return <View
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                color={colors.project.gold}
                size="large"
            />
        </View>;
    }

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        {renderHeader()}
        {renderList()}
        {renderFormDialog()}
        {renderDeleteDialog()}
    </View>;
};
export default BoardMembersView;
